/**
 * Turns a stored schedule into the days it is next going to run on.
 *
 * The summary panel's calendar wants an array of "YYYY-MM-DD" strings. The scheduler
 * returns no such list — it returns the rule, and `RunTime` is always the zero timestamp
 * — so the dates are worked out here from the recurrence the schedule was saved with.
 *
 * ── SHAPE OF THE ANSWER ──────────────────────────────────────────────────────
 * Pure: everything comes from the record and the injected `now`, the same way
 * buildSchedulePayload takes `now` so it can be tested without waiting for tomorrow.
 *
 * Nothing here throws. A malformed, legacy or unrecognised record yields [] and the panel
 * says "No upcoming runs" — which is the honest answer when we cannot read the rule.
 *
 * ── ONE-WAY IMPORT ──────────────────────────────────────────────────────────
 * This imports `activeRecurrence` from scheduleMappers. scheduleMappers must NOT import
 * this module back: that one rule is all that keeps the pair out of an import cycle, and
 * it is the second reason the expansion does not live inside rowFromSchedule (the first
 * is that a legacy schedule's end date only exists on the record GET /id returns, so a
 * list row cannot be expanded correctly anyway).
 *
 * ── WHY A WINDOW AND NOT A COUNT ────────────────────────────────────────────
 * Neither bound works alone. "Next 30 occurrences" of a daily schedule covers one month,
 * so paging the calendar to month +2 shows an empty grid for a schedule that certainly
 * runs then — it reads as broken. "Next 12 months" alone returns nothing for a rule that
 * fires less often than that, so a live schedule reports no upcoming runs.
 *
 * So: a 12-month window, floored at "at least the next occurrence". Twelve months is the
 * smallest window that guarantees a yearly rule shows something; past a year the answer
 * is speculative anyway, since such a schedule will likely be edited or ended first.
 */

import moment from "moment";

import { activeRecurrence, customRecurrenceType, isCustomSchedule } from "./scheduleMappers";
import { DATE_FORMAT } from "../../constants/dateConstants";

/* The dense window every rule is walked across. */
export const HORIZON_MONTHS = 12;

/* Walked only when the window above found nothing at all, so a rule that fires less
   often than yearly still shows its next run rather than claiming it has none. */
export const SPARSE_HORIZON_MONTHS = 60;

/* A leap year of daily runs is 366; this leaves slack without letting a pathological
   record fill memory. */
export const MAX_DATES = 400;

/* Iteration ceiling, for a record that somehow advances without terminating. */
export const MAX_STEPS = 3000;

/* Above this, an occurrence cap is treated as no cap. Counting from the first run means
   walking every occurrence, and 5000 daily runs is over thirteen years - far longer for
   any other type - so a cap this large cannot bite inside a 12-month window unless the
   record predates this codebase. */
export const MAX_COUNT_WALK = 5000;

/**
 * What to do with a monthly schedule set to a day its month does not have - the 31st in
 * February, say.
 *
 * "skip": that month gets no run. "clamp": it runs on the last day instead.
 *
 * Skip, because the calendar is a prediction of what the scheduler's cron actually does
 * and nobody has told us which it is. Given the choice, a marked day that never fires is
 * the worse error - people plan around marked days - while a missing mark is only an
 * omission. It is also what Google Calendar does, so it is the behaviour most people have
 * already met.
 *
 * TODO: confirm with the scheduler team. Whatever their cron does is the truth; this is a
 * considered guess until someone asks.
 */
export const SHORT_MONTH = "skip";

/* The stored strings are the API's, not the form's - RepeatOnDays holds "Monday" and
   MonthlyOnDays.Week holds "First". Small local tables rather than inverting the form's
   code->label maps in RecurrencePatternsForms/options.js, which run the other way. */
const WEEKDAY_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const ORDINAL_INDEX = { first: 0, second: 1, third: 2, fourth: 3, last: -1 };

/* RecurrencePattern on a legacy record, or Custom.RecurrenceType on a current one. */
const TYPE_BY_NAME = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
  annually: "Yearly",
};

const UNIT_BY_TYPE = { Daily: "days", Weekly: "days", Monthly: "months", Yearly: "years" };

const lower = (value) => String(value === null || value === undefined ? "" : value).trim().toLowerCase();

const weekdayFromName = (name) => {
  const index = WEEKDAY_INDEX[lower(name)];
  return index === undefined ? null : index;
};

/** MonthlyOnDays.Day as a day number, or null when it holds a weekday name instead. */
const dayOfMonthFrom = (value) => {
  const day = Number(String(value === null || value === undefined ? "" : value).trim());
  return Number.isInteger(day) && day >= 1 && day <= 31 ? day : null;
};

/* Strict, with the format named. Non-strict moment falls back to the Date constructor,
   which accepts "2026-13-45" and logs a deprecation warning for the trouble. */
const strictDay = (value) => {
  if (!value) return null;
  const parsed = moment(String(value), DATE_FORMAT, true);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

/**
 * A date from a field that may be a plain day or a full ISO timestamp.
 *
 * Rejects anything at or before 1970: legacy records carry "0001-01-01T00:00:00Z" for a
 * date they do not have, and moment parses that quite happily. Taken as an end date it
 * would report every legacy schedule as long finished. Same zero-timestamp problem
 * createdTimestamp already deals with in scheduleMappers.
 */
const usableDate = (value) => {
  if (!value) return null;

  const asDay = strictDay(value);
  if (asDay) return asDay.year() > 1970 ? asDay : null;

  const parsed = moment(String(value), moment.ISO_8601, true);
  if (!parsed.isValid() || parsed.year() <= 1970) return null;
  return parsed.startOf("day");
};

/* ── WHEN THE SCHEDULE STOPS ────────────────────────────────────────────────
   Custom.Ends is the current shape and always carries all three keys, so the order
   matters: never, then a date, then a number of runs.

   The top-level trio is the fallback for a legacy record with no Ends block. It says the
   same thing in the older spelling, where ScheduleEndCondition is 1 = ends on a date,
   2 = after N executions, 3 = never. */
const endsFrom = (record, block) => {
  const none = { endAt: null, endAfter: 0 };
  const ends = block && block.Ends;

  if (ends) {
    if (ends.IsEndsNever) return none;
    const endAt = usableDate(ends.EndAt);
    if (endAt) return { endAt, endAfter: 0 };
    const endAfter = Math.floor(Number(ends.EndAfterOccurrences) || 0);
    if (endAfter > 0) return { endAt: null, endAfter };
    // all three blank: fall through. A record this app wrote cannot reach here.
  }

  if (Number(record?.ScheduleEndCondition) === 3) return none;

  const validTo = usableDate(record?.ValidTo);
  if (validTo) return { endAt: validTo, endAfter: 0 };

  const limit = Math.floor(Number(record?.ExecutionLimit) || 0);
  if (limit > 0) return { endAt: null, endAfter: limit };

  return none;
};

/**
 * The record's recurrence as one flat rule, so the walk below knows nothing about the
 * API's spelling or its legacy shapes.
 *
 * Normalising first is what keeps every malformed and legacy case to a line or two, and
 * what makes them testable without any date arithmetic.
 *
 * Exported for the tests.
 */
export function ruleFromRecord(record) {
  const block = activeRecurrence(record);
  const start = strictDay(block.StartAt) || strictDay(record?.ValidFrom);

  // Nothing to anchor on. Walking from today instead would invent a schedule.
  if (!start) return null;

  const noEnds = { weekdays: [], monthDay: null, ordinal: null, ordinalWeekday: null, endAt: null, endAfter: 0 };

  if (record?.RecurrencePattern === "NotRepeat") {
    return { type: "Once", interval: 1, start, ...noEnds };
  }

  /* A custom schedule's cadence is read out of its block, never off the top-level
     pattern. From 2026-09-11 the two agree - the payload copies the cadence up to
     RecurrencePattern - but every schedule saved before that carries the literal
     "Custom" up there, which names no cadence at all. Inside the block the key is
     RecurrencePattern on new records and RecurrenceType on old ones, and
     customRecurrenceType reads either. */
  const rawType = isCustomSchedule(record) ? customRecurrenceType(block) : record?.RecurrencePattern;
  const type = TYPE_BY_NAME[lower(rawType)];

  /* An unrecognised pattern still has a start date, and one run on that day is a better
     answer than none - it is the only thing the record actually tells us. */
  if (!type) return { type: "Once", interval: 1, start, ...noEnds };

  /* ── TWO SPELLINGS FOR THE SAME TWO THINGS ──
     The Custom block says RecurrenceInterval and RepeatOnDays; the simple Daily and
     Weekly blocks say Frequency and WeekDay/WeekDays. Both are read here because both
     reach this function - a schedule built from the Repeat dropdown fills a simple
     block, one built in the dialog fills Custom.

     Reading only Custom's names is quiet rather than loud, which is why it is worth
     spelling out: a Monday-to-Friday schedule would find no days, fall through to the
     "use the start date's weekday" guard below, and draw one day a week on a calendar
     that should show five.

     0 would advance nowhere and loop forever; a negative or fractional one is nonsense. */
  const rawInterval = block.RecurrenceInterval ?? block.Frequency;
  const interval = Math.max(1, Math.floor(Number(rawInterval) || 1));

  const dayNames = [...(block.RepeatOnDays || []), ...(block.WeekDays || []), block.WeekDay];
  let weekdays = dayNames.map(weekdayFromName).filter((day) => day !== null);
  weekdays = [...new Set(weekdays)].sort((a, b) => a - b);
  /* A Weekly rule with nothing to match would advance forever emitting nothing. Its start
     date's weekday is the one day we know it runs. */
  if (type === "Weekly" && !weekdays.length) weekdays = [start.day()];

  const onDays = block.MonthlyOnDays || {};
  const ordinalRaw = ORDINAL_INDEX[lower(onDays.Week)];
  const isOrdinal = ordinalRaw !== undefined;

  return {
    type,
    interval,
    start,
    weekdays,
    /* Day does double duty: a day number when Week is empty, a weekday name when Week
       holds an ordinal. A non-empty, recognised Week is the only discriminator. */
    monthDay: isOrdinal ? null : dayOfMonthFrom(onDays.Day) ?? start.date(),
    ordinal: isOrdinal ? ordinalRaw : null,
    ordinalWeekday: isOrdinal ? weekdayFromName(onDays.Day) ?? start.day() : null,
    ...endsFrom(record, block),
  };
}

/**
 * The nth given weekday of a month — "the first Monday", "the last Friday".
 *
 * `ordinal` is 0-based, or -1 for last. Returns null only for an out-of-band ordinal:
 * first through fourth always exist, since the worst case is 1 + 6 + 21 = the 28th and
 * every month has 28 days.
 *
 * Exported for the tests.
 */
export function nthWeekdayOfMonth(monthAnchor, weekday, ordinal) {
  if (weekday === null || weekday === undefined) return null;

  if (ordinal === -1) {
    const last = monthAnchor.clone().endOf("month").startOf("day");
    return last.clone().subtract((last.day() - weekday + 7) % 7, "days");
  }

  const first = monthAnchor.clone().startOf("month");
  const date = 1 + ((weekday - first.day() + 7) % 7) + ordinal * 7;
  return date <= first.daysInMonth() ? first.clone().date(date) : null;
}

/** The chosen day of a month, or null when that month is too short — see SHORT_MONTH. */
const dayOrSkip = (monthAnchor, day) => {
  const inMonth = monthAnchor.daysInMonth();
  if (day <= inMonth) return monthAnchor.clone().date(day);
  return SHORT_MONTH === "clamp" ? monthAnchor.clone().date(inMonth) : null;
};

/**
 * The run dates inside one period of the rule, in ascending order.
 *
 * Ascending matters: both the occurrence counter and the output cut below depend on
 * reaching dates in the order they happen.
 */
const datesInPeriod = (rule, cursor) => {
  if (rule.type === "Daily") return [cursor.clone()];

  if (rule.type === "Weekly") {
    /* Offsets measured from the cursor, not from startOf("week"). Two reasons: the
       locale's first day of the week does not come into it, and each period covers
       exactly cursor..cursor+6, so periods tile with no overlap at interval 1 and leave
       the right gap above it.

       The case that catches a startOf("week") version: "every weekday" is stored as
       Weekly Mon-Fri interval 1. Starting on a Wednesday, this yields Wed Thu Fri then
       Mon Tue of the next calendar week, and the following period starts on the next
       Wednesday - continuous, no duplicates, no gap. */
    return rule.weekdays
      .map((weekday) => cursor.clone().add((weekday - cursor.day() + 7) % 7, "days"))
      .sort((a, b) => a.valueOf() - b.valueOf());
  }

  // Monthly and Yearly: the cursor is the 1st, so the month is fixed either way.
  const date =
    rule.ordinal !== null
      ? nthWeekdayOfMonth(cursor, rule.ordinalWeekday, rule.ordinal)
      : dayOrSkip(cursor, rule.monthDay);

  return date ? [date] : [];
};

/* Monthly and Yearly cursors sit on the 1st so nothing clamps; see startCursor. */
const advance = (rule, cursor) => {
  if (rule.type === "Weekly") return cursor.clone().add(rule.interval * 7, "days");
  return cursor.clone().add(rule.interval, UNIT_BY_TYPE[rule.type]);
};

/**
 * Where to begin walking.
 *
 * ── THE CURSOR IS A PERIOD ANCHOR, NEVER A RUN DATE ──
 * For Monthly and Yearly it is always the 1st of the month. Carrying the day of the month
 * on the cursor drifts: moment("2026-01-31").add(1, "month") is 2026-02-28, and the next
 * step is 2026-03-28 — a schedule set to the 31st quietly becomes one set to the 28th.
 * Keeping the day off the cursor and re-deriving it each period removes that whole class
 * of bug.
 *
 * ── AND WHY THE PHASE JUMP IS CONDITIONAL ──
 * An occurrence cap counts from the very first run, past ones included, so when there is
 * one the walk has to start at the beginning and cannot skip forward. Without a cap the
 * cursor jumps to the last period starting on or before today, which keeps the interval's
 * phase ("every 3 days from the 1st" stays on the 1st, 4th, 7th) while bounding the walk.
 */
const startCursor = (rule, today) => {
  const base =
    rule.type === "Monthly" || rule.type === "Yearly"
      ? rule.start.clone().startOf("month")
      : rule.start.clone();

  if (rule.endAfter > 0 && rule.endAfter <= MAX_COUNT_WALK) return base;

  const unit = UNIT_BY_TYPE[rule.type];
  const step = rule.type === "Weekly" ? rule.interval * 7 : rule.interval;
  const elapsed = today.diff(base, unit);

  // Floor, so the cursor never steps past an occurrence that is still to come.
  const periods = Math.max(0, Math.floor(elapsed / step));
  return periods > 0 ? base.clone().add(periods * step, unit) : base;
};

/**
 * The days this schedule is next due to run, ascending, as "YYYY-MM-DD".
 *
 * Past runs are left out — the panel's heading is "Upcoming Schedules" — but they are
 * still counted against an occurrence cap, because a schedule set to stop after ten runs
 * that has already fired seven has three left, not ten.
 *
 * Today counts as upcoming whatever RunAt says. The calendar marks whole days, and
 * comparing against the run time would need the schedule's own timezone to be right.
 */
export function upcomingRunDates(record, { now = new Date(), horizonMonths = HORIZON_MONTHS, maxDates = MAX_DATES } = {}) {
  const rule = ruleFromRecord(record);
  if (!rule) return [];

  const today = moment(now).startOf("day");
  const todayKey = today.format(DATE_FORMAT);

  // Already over. Cheaper than discovering it one step at a time.
  if (rule.endAt && rule.endAt.isBefore(today, "day")) return [];

  if (rule.type === "Once") {
    const key = rule.start.format(DATE_FORMAT);
    return key >= todayKey ? [key] : [];
  }

  const windowEnd = today.clone().add(horizonMonths, "months").endOf("month");
  const hardEnd = today.clone().add(SPARSE_HORIZON_MONTHS, "months");

  const out = [];
  const seen = new Set();
  let occurrences = 0;
  let cursor = startCursor(rule, today);

  for (let steps = 0; steps < MAX_STEPS; steps += 1) {
    if (rule.endAt && cursor.isAfter(rule.endAt, "day")) break;

    if (cursor.isAfter(windowEnd)) {
      // The window is satisfied; only keep going for a rule that fired nothing in it.
      if (out.length) break;
      if (cursor.isAfter(hardEnd)) break;
    }

    for (const date of datesInPeriod(rule, cursor)) {
      // The first period can reach back before the schedule began.
      if (date.isBefore(rule.start, "day")) continue;
      if (rule.endAt && date.isAfter(rule.endAt, "day")) return out;

      occurrences += 1;
      if (rule.endAfter && occurrences > rule.endAfter) return out;

      const key = date.format(DATE_FORMAT);
      if (key < todayKey || seen.has(key)) continue;
      if (out.length >= maxDates) return out;

      seen.add(key);
      out.push(key);
    }

    cursor = advance(rule, cursor);
  }

  return out;
}

/** The single next run, for anywhere that wants one date rather than a calendar. */
export function nextRunDate(record, options = {}) {
  const [next] = upcomingRunDates(record, { ...options, maxDates: 1 });
  return next || null;
}

export default upcomingRunDates;
